import { LocalStorage } from 'quasar'
import { LOCAL_STORAGE_KEYS } from 'src/boot/constants'
import { useClientContext } from 'src/composables/useClientContext'
import { computed } from 'vue'

/** @type {Readonly<string[]>} */
let previousPreviewedPermissions = Object.freeze([])
let hasSetPreviewedPermissionsAtBoot = false

/**
 * Initializes and returns the current previewed permissions, if allowed.
 * For wide accessibility, this value is stored in `Model.context.previewedPermittedFrontendActions`.
 * The Model.context is awaited.
 */
export default function usePreviewPermissions () {
  const { clientContext, isLoaded, ensureBooted } = useClientContext()

  if (!hasSetPreviewedPermissionsAtBoot) {
    let storedPreviewedPermissions = LocalStorage.getItem(LOCAL_STORAGE_KEYS.previewedPermissions)
    storedPreviewedPermissions = _.isArray(storedPreviewedPermissions) ? storedPreviewedPermissions : []
    previousPreviewedPermissions = Object.freeze(storedPreviewedPermissions)
    ensureBooted().then(() => {
      if (clientContext.value.isPreviewPermissionsPermitted && !_.isEmpty(previousPreviewedPermissions)) {
        clientContext.value.previewPermissions(previousPreviewedPermissions)
      } else {
        clientContext.value.stopPreviewingPermissions()
      }
    })
    hasSetPreviewedPermissionsAtBoot = true
  }

  const previewedPermissions = computed(() => {
    return isLoaded.value ? clientContext.value.previewedPermittedFrontendActions : []
  })

  /** @param {string[]} permissionActions */
  async function previewPermissions (permissionActions) {
    await ensureBooted()
    clientContext.value.previewPermissions(permissionActions)
  }

  /**
   * Simulate the user not having any permissions. We must set a non-empty
   * permissions array for it to take effect, so we use a dummy permission name.
   */
  async function previewEmptyPermissions () {
    await ensureBooted()
    clientContext.value.previewPermissions(['none'])
  }

  async function stopPreviewingPermissions () {
    await ensureBooted()
    clientContext.value.stopPreviewingPermissions()
  }

  return {
    previewedPermissions,
    previousPreviewedPermissions,
    previewPermissions,
    previewEmptyPermissions,
    stopPreviewingPermissions
  }
}
